import { apiSlice } from './apiSlice';
import { financeApi } from './financeApi';
import { notificationListTag, notificationsApi } from './notificationsApi';
import { Document } from '../../types';

export interface DocumentsResponse {
  documents: Document[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UploadDocumentRequest {
  applicationId?: number;
  routeId?: number;
  cargoId?: number;
  entityType?: 'driver' | 'vehicle';
  entityId?: number;
  stage?: 'loading' | 'unloading' | 'other';
  comment?: string;
  fileType: 'file' | 'photo';
  documentType?: string;
  /** Дата получения документа (для триггеров обязательств). Опционально. */
  receivedDate?: string | null;
  /** Для ORIGINAL_TN: способ получения (COURIER | MAIL | HAND) */
  receivedMethod?: string;
  /** Для ORIGINAL_TN: трек-номер (опционально) */
  trackingNumber?: string;
  /** Реквизиты счета (обязательны для типов INVOICE/CARRIER_INVOICE/LOADING_INVOICE) */
  invoiceNumber?: string;
  invoiceDate?: string | null;
  invoiceAmount?: number | null;
  /** Дата отправки клиенту (обязательно для INVOICE / счёт клиенту) */
  sentToClientAt?: string | null;
  /** Срок оплаты (опционально для INVOICE) */
  dueDate?: string | null;
  /** Один файл или массив файлов */
  file?: File;
  files?: File[];
  /** Имя файла для сохранения (опционально) */
  originalName?: string;
  /** Обойти проверку дубликата счёта (та же заявка + сумма) */
  force?: boolean;
  /** Phase DOCUMENT-UPLOAD-SCOPE-FIX — scoped task closure (not persisted on Document). */
  groupId?: number;
  routeStopId?: number;
  customDocumentName?: string;
  customDocumentCategory?: string;
}

export interface DocumentFilters {
  applicationId?: number;
  routeId?: number;
  cargoId?: number;
  stage?: 'loading' | 'unloading' | 'other';
  fileType?: 'file' | 'photo';
  dateFrom?: string;
  dateTo?: string;
}

export interface CreateDocumentDispatchRequest {
  applicationId: number;
  dispatchType: string;
  dispatchedAt: string;
  documentIds: number[];
  comment?: string;
  taskType?: string;
  metadata?: Record<string, unknown>;
}

export const documentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Получение списка документов
    getDocuments: builder.query<DocumentsResponse, DocumentFilters & {
      page?: number;
      limit?: number;
    }>({
      query: ({ page = 1, limit = 10, ...filters }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });

        return `/documents?${params.toString()}`;
      },
      transformResponse: (response: any): DocumentsResponse => {
        // transform response to DocumentsResponse
        if (response.data) {
          return {
            documents: response.data.documents || [],
            pagination: response.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
          };
        }
        return response;
      },
      providesTags: ['Document'],
    }),

    // Получение документов по заявке
    getDocumentsByApplication: builder.query<Document[], number>({
      query: (applicationId) => `/documents/application/${applicationId}`,
      transformResponse: (response: any) => {
        return response.data || response;
      },
      providesTags: (result, error, applicationId) => [
        { type: 'Document', id: `application-${applicationId}` }
      ],
    }),

    // Получение статистики документов по заявке
    getApplicationDocumentsStats: builder.query<{
      totalDocuments: number;
      applicationDocuments: number;
      routeDocuments: number;
      photos: number;
      files: number;
      routesCount: number;
    }, number>({
      query: (applicationId) => `/documents/application/${applicationId}/stats`,
      transformResponse: (response: any) => {
        return response.data || response;
      },
      providesTags: (result, error, applicationId) => [
        { type: 'Document', id: `application-stats-${applicationId}` }
      ],
    }),

    // Получение документов по маршруту
    getDocumentsByRoute: builder.query<Document[], number>({
      query: (routeId) => `/documents/route/${routeId}`,
      transformResponse: (response: any) => {
        return response.data || response;
      },
      providesTags: (result, error, routeId) => [
        { type: 'Document', id: `route-${routeId}` }
      ],
    }),

    // Получение документов по грузу
    getDocumentsByCargo: builder.query<Document[], number>({
      query: (cargoId) => `/documents/cargo/${cargoId}`,
      transformResponse: (response: any) => {
        return response.data || response;
      },
      providesTags: (result, error, cargoId) => [
        { type: 'Document', id: `cargo-${cargoId}` }
      ],
    }),

    getDocumentsByEntity: builder.query<Document[], { entityType: 'driver' | 'vehicle'; entityId: number }>({
      query: ({ entityType, entityId }) => `/documents/entity/${entityType}/${entityId}`,
      transformResponse: (response: any) => {
        return response.data || response;
      },
      providesTags: (result, error, { entityType, entityId }) => [
        { type: 'Document', id: `${entityType}-${entityId}` }
      ],
    }),

    // Загрузка документа(ов)
    uploadDocument: builder.mutation<Document[], UploadDocumentRequest>({
      query: (data) => {
        const formData = new FormData();
        const files = data.files?.length ? data.files : (data.file ? [data.file] : []);
        if (files.length === 0) throw new Error('Файл не выбран');
        files.forEach((f) => formData.append('files', f));
        
        // Добавляем остальные поля
        if (data.applicationId) formData.append('applicationId', data.applicationId.toString());
        if (data.routeId) formData.append('routeId', data.routeId.toString());
        if (data.groupId) formData.append('groupId', data.groupId.toString());
        if (data.routeStopId) formData.append('routeStopId', data.routeStopId.toString());
        if (data.cargoId) formData.append('cargoId', data.cargoId.toString());
        if (data.entityType) formData.append('entityType', data.entityType);
        if (data.entityId) formData.append('entityId', data.entityId.toString());
        if (data.stage) formData.append('stage', data.stage);
        if (data.comment) formData.append('comment', data.comment);
        if (data.documentType) formData.append('documentType', data.documentType);
        if (data.originalName) formData.append('originalName', data.originalName);
        if (data.receivedDate != null && data.receivedDate !== '') formData.append('receivedDate', data.receivedDate);
        if (data.receivedMethod) formData.append('receivedMethod', data.receivedMethod);
        if (data.trackingNumber) formData.append('trackingNumber', data.trackingNumber);
        if (data.invoiceNumber) formData.append('invoiceNumber', data.invoiceNumber);
        if (data.invoiceDate) formData.append('invoiceDate', data.invoiceDate);
        if (data.invoiceAmount != null) formData.append('invoiceAmount', data.invoiceAmount.toString());
        if (data.sentToClientAt) formData.append('sentToClientAt', data.sentToClientAt);
        if (data.dueDate) formData.append('dueDate', data.dueDate);
        if (data.force) formData.append('force', 'true');
        if (data.customDocumentName) formData.append('customDocumentName', data.customDocumentName);
        if (data.customDocumentCategory) formData.append('customDocumentCategory', data.customDocumentCategory);
        formData.append('fileType', data.fileType);
        // Support marking uploaded document as primary docs
        if ((data as any).isPrimaryDocs) {
          formData.append('isPrimaryDocs', 'true');
        }

        const request = {
          url: '/documents/upload',
          method: 'POST',
          body: formData,
        };
        return request;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // После успешной загрузки документа инвалидация финансовых данных
          dispatch(financeApi.util.invalidateTags(['Finance']));
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));

          // Если была передана applicationId — инвалидация списка документов по этой заявке
          try {
            const applicationId = (arg as any).applicationId;
            if (applicationId) {
              dispatch(documentsApi.util.invalidateTags([{ type: 'Document', id: `application-${applicationId}` }] as any));
            }
            if ((arg as any).entityType && (arg as any).entityId) {
              dispatch(documentsApi.util.invalidateTags([
                { type: 'Document', id: `${(arg as any).entityType}-${(arg as any).entityId}` }
              ] as any));
            }
          } catch (e) {
            console.warn('Failed to invalidate documents by application', e);
          }
        } catch {}
      },
      transformResponse: (response: any) => {
        const data = response.data;
        return Array.isArray(data) ? data : (data ? [data] : []);
      },
      invalidatesTags: (result, error, arg) => [
        'Document',
        'Route',
        'Workspace',
        'ApplicationControlTasks',
        notificationListTag,
        ...(arg.applicationId != null
          ? [{ type: 'ClosingPackage' as const, id: arg.applicationId }]
          : []),
      ],
    }),

    // Скачивание документа
    downloadDocument: builder.query<Blob, number>({
      query: (id) => ({
        url: `/documents/${id}/download`,
        responseHandler: (response) => response.blob(),
      }),
      providesTags: (result, error, id) => [{ type: 'Document', id }],
    }),

    createDocumentDispatch: builder.mutation<any, CreateDocumentDispatchRequest>({
      query: (body) => ({
        url: '/documents/dispatch',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'Document',
        'Workspace',
        'Application',
        'ApplicationControlTasks',
        notificationListTag,
        ...(arg.applicationId != null
          ? [{ type: 'ClosingPackage' as const, id: arg.applicationId }]
          : []),
      ],
    }),

    // Удаление документа
    deleteDocument: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/documents/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Инвалидация финансовых данных после удаления
          dispatch(financeApi.util.invalidateTags(['Finance']));
        } catch {}
      },
      invalidatesTags: ['Document', 'Route', 'Workspace'],
    }),
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentsByApplicationQuery,
  useGetApplicationDocumentsStatsQuery,
  useGetDocumentsByRouteQuery,
  useGetDocumentsByCargoQuery,
  useGetDocumentsByEntityQuery,
  useUploadDocumentMutation,
  useDownloadDocumentQuery,
  useCreateDocumentDispatchMutation,
  useDeleteDocumentMutation,
} = documentsApi;